import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { ConnectionState, NotificationEvents } from 'types/pushNotifications';
import { receiveNotifications, stopNotifications } from 'utils/signalrConnector';
import { AppAlert } from 'components/app-alert/app-alert';
import useBrowserLock from 'hooks/useBrowserLock';
import useHubConnection from 'hooks/useHubConnection';

const getJoinedId = (id?: (string | undefined)[]) => {
  if (!id || id.length === 0) return undefined;
  return id.filter((i) => i !== undefined).join('-');
};

export interface HubEventHandler {
  eventType: string;
  handler: (event: NotificationEvents, signal?: AbortSignal) => void;
  id?: (string | undefined)[];
}

interface SetupNotificationsLayoutProps {
  hubName: string;
  children: ReactNode;
  eventHandlers?: HubEventHandler[];
  onConnected?: (signal?: AbortSignal) => void;
  onReconnected?: (signal?: AbortSignal) => void;
  onDisconnected?: (signal?: AbortSignal) => void;
}

export const SetupNotificationsLayout = ({
  hubName,
  children,
  eventHandlers = [],
  onConnected,
  onReconnected,
  onDisconnected,
}: SetupNotificationsLayoutProps) => {
  const registeredEventsRef = useRef<Set<string>>(new Set());
  const hasConnectedRef = useRef(false);
  const connectionWasInterruptedRef = useRef(false);
  const eventHandlerRefsRef = useRef<Map<string, (...events: NotificationEvents[]) => void>>(
    new Map()
  );

  useBrowserLock();

  const hubInfo = useHubConnection(hubName);
  const queryClient = useQueryClient();

  // Cancel the retired authority before replacement query subscriptions can start.
  useLayoutEffect(() => {
    if (!hubInfo.authorityKey) return;
    return () => {
      void queryClient.cancelQueries();
    };
  }, [hubInfo.authorityKey, queryClient]);

  useEffect(() => {
    const connection = hubInfo.hubConnection;
    if (
      !connection ||
      hubInfo.connectionState !== ConnectionState.Connected ||
      eventHandlers.length === 0
    ) {
      return;
    }

    const registeredEvents = registeredEventsRef.current;
    const eventHandlerRefs = eventHandlerRefsRef.current;

    let disposed = false;
    eventHandlers.forEach((handler) => {
      const eventId = getJoinedId(handler.id);
      const registrationKey = `${hubName}-${handler.eventType}${eventId ? `-${eventId}` : ''}`;

      if (registeredEvents.has(registrationKey)) return;

      const wrappedHandler = (...events: NotificationEvents[]) => {
        if (disposed || hubInfo.signal?.aborted) return;
        events.forEach((event) => handler.handler(event, hubInfo.signal));
      };

      eventHandlerRefs.set(registrationKey, wrappedHandler);

      receiveNotifications(connection, handler.eventType, wrappedHandler);

      registeredEvents.add(registrationKey);
    });

    // Cleanup function to unregister handlers
    return () => {
      disposed = true;
      if (!connection) return;

      eventHandlers.forEach((handler) => {
        const eventId = getJoinedId(handler.id);
        const registrationKey = `${hubName}-${handler.eventType}${eventId ? `-${eventId}` : ''}`;

        const wrappedHandler = eventHandlerRefs.get(registrationKey);
        if (wrappedHandler) {
          stopNotifications(connection, handler.eventType, wrappedHandler);
          eventHandlerRefs.delete(registrationKey);
          registeredEvents.delete(registrationKey);
          console.log(`Unregistered event handler: ${registrationKey}`);
        }
      });
    };
  }, [hubInfo.hubConnection, hubInfo.connectionState, eventHandlers, hubName, hubInfo.signal]);

  // A replacement connection starts a fresh lifecycle, not a reconnect of prior authority.
  useEffect(() => {
    hasConnectedRef.current = false;
    connectionWasInterruptedRef.current = false;
  }, [hubInfo.hubConnection]);

  // Lifecycle callbacks
  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Connected && onConnected) {
      onConnected(hubInfo.signal);
    }
  }, [hubInfo.connectionState, onConnected, hubInfo.signal]);

  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Connected) {
      if (!hasConnectedRef.current) {
        hasConnectedRef.current = true;
        return;
      }

      if (connectionWasInterruptedRef.current) {
        connectionWasInterruptedRef.current = false;
        onReconnected?.(hubInfo.signal);
      }

      return;
    }

    if (
      hasConnectedRef.current &&
      (hubInfo.connectionState === ConnectionState.Reconnecting ||
        hubInfo.connectionState === ConnectionState.Disconnected)
    ) {
      connectionWasInterruptedRef.current = true;
    }
  }, [hubInfo.connectionState, onReconnected, hubInfo.signal]);

  useEffect(() => {
    if (hubInfo.connectionState === ConnectionState.Disconnected && onDisconnected) {
      onDisconnected(hubInfo.signal);
    }
  }, [hubInfo.connectionState, onDisconnected, hubInfo.signal]);

  const appAlert =
    hubInfo.connectionState === ConnectionState.Reconnecting ? (
      <AppAlert text="Staff Portal is reconnecting..." />
    ) : hubInfo.connectionState === ConnectionState.Disconnected ? (
      <AppAlert text="Staff Portal disconnected. Try reload the web page." />
    ) : null;

  if (!hubName) {
    console.warn('No hub name provided for SetupNotificationsLayout');
    return <>{children}</>;
  }

  return (
    <>
      {appAlert}
      {children}
    </>
  );
};
